import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  buildLevelTestReportByCodeAPI,
  fetchCodeVaultAPI,
  fetchLevelTestReportStatusByCodeAPI,
} from "../levelTestAPI";
import "./LevelTestCodeVault.css";

// 코드함. 계약하지 않은 학원(라이트 등급)이 보는 화면이다.
//
// 코드는 자격과 무관하게 나가는데(설문 이벤트, 개인 판매, 체험 1장) 관리 화면은
// 계약한 학원만 열려서, 코드를 쥐고 학생이 응시까지 마쳤는데 원장이 결과를 볼 데가
// 없었다. 그 사이를 메우는 화면이라 **코드와 리포트만** 보여준다.
//
// 잠긴 기능은 `disabled` 로 막지 않는다. disabled 인 버튼은 onClick 이 아예 안 걸려서
// 왜 못 쓰는지 알려줄 수가 없다. 눌리게 두고 안내를 띄운다.

// 만료가 이만큼 남으면 붉게 띄운다. 코드는 안 쓰면 그냥 사라지는 물건이라
// 학원이 남은 날을 알아야 한다.
const EXPIRY_WARNING_DAYS = 30;

// 리포트가 다 됐는지 물어보는 간격과 횟수. 굽는 데 1분 안팎이라 5초씩 5분까지 본다.
// 그 안에 안 끝나도 서버는 계속 굽고 있으므로, 화면을 다시 열면 리포트가 붙어 있다.
const REPORT_POLL_MS = 5000;
const REPORT_POLL_MAX = 60;

const LOCKED_FEATURE_LIST = [
  {
    title: "재원생 관리",
    summary: "달마다 코드 자동 배정, 응시 현황 추적",
    detail:
      "원생 명단을 올려두면 달마다 응시코드가 자동으로 배정됩니다. 누가 받았고 누가 아직 안 봤는지 한 화면에서 보입니다.",
  },
  {
    title: "응시 안내 알림톡",
    summary: "학부모에게 코드와 주소를 한 번에 발송",
    detail:
      "학부모에게 코드와 응시 주소를 알림톡으로 한 번에 보냅니다. 원장님이 한 명씩 문자로 보낼 필요가 없습니다.",
  },
  {
    title: "신규생 관리",
    summary: "테스트 응시자를 원생으로 전환",
    detail:
      "레벨테스트를 보러 온 신규생을 등록해 두고, 결과를 본 뒤 재원생으로 전환할 수 있습니다. 상담 기록이 남습니다.",
  },
  {
    title: "학습 센터",
    summary: "단어·듣기·문법·문해력 복습과 진도",
    detail:
      "시험이 끝나면 학생이 틀린 것만 골라 복습합니다. 단어, 듣기, 문법, 문해력 네 갈래로 진도가 쌓입니다.",
  },
];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
};

const daysLeft = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date - today) / 86400000);
};

// demoData 를 주면 서버를 부르지 않고 그 값으로 그린다. 로그인 없이 여는 따라하기 안내(/demo/leveltest)가 쓴다
const LevelTestCodeVault = ({ demoData = null }) => {
  const [data, setData] = useState(demoData);
  const [error, setError] = useState("");
  const [copiedCode, setCopiedCode] = useState("");
  const [openedFeature, setOpenedFeature] = useState(null);
  // 지금 리포트를 만들고 있는 코드. 한 번에 하나만 만든다.
  const [buildingCode, setBuildingCode] = useState("");
  // 다 됐는지 물어보는 타이머. 화면을 떠날 때 걷어낸다.
  const pollTimerRef = useRef({});

  const reload = () => {
    if (demoData) return Promise.resolve();
    return fetchCodeVaultAPI()
      .then(setData)
      .catch(() => {});
  };

  // 응시를 끝내면 리포트가 저절로 구워진다. 굽는 줄이 있으면 다 될 때까지 목록을 다시 받는다.
  const hasBuildingReport = (data?.used || []).some(
    (item) => item.report_building
  );
  useEffect(() => {
    if (!hasBuildingReport) return undefined;
    const timer = setTimeout(reload, REPORT_POLL_MS * 2);
    return () => clearTimeout(timer);
  }, [hasBuildingReport, data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timerMap = pollTimerRef.current;
    return () => {
      Object.values(timerMap).forEach(clearTimeout);
    };
  }, []);

  /**
   * 다 됐는지 되물어본다. **굽는 것은 서버가 별도 프로세스로 한다.** 한 아이에 AI 를
   * 두 번 부르므로 1분 안팎이 걸리는데, 그동안 요청을 붙잡고 있으면 gunicorn 이
   * 30초에서 잘라 버려 리포트가 끝내 안 생겼다. 이 화면을 닫아도 굽기는 이어진다.
   */
  const pollReport = (code, tryCount) => {
    pollTimerRef.current[code] = setTimeout(() => {
      fetchLevelTestReportStatusByCodeAPI(code)
        .then((result) => {
          if (result.status === "building") {
            if (tryCount < REPORT_POLL_MAX) pollReport(code, tryCount + 1);
            return;
          }
          setBuildingCode("");
          if (result.status === "failed") {
            alert(result.error || "리포트를 만들지 못했습니다.");
            return;
          }
          reload();
          if (result.report_url) window.open(result.report_url, "_blank");
        })
        .catch(() => {
          // 한 번 못 물어본 것으로 그만두지 않는다. 다음 차례에 다시 묻는다.
          if (tryCount < REPORT_POLL_MAX) pollReport(code, tryCount + 1);
        });
    }, REPORT_POLL_MS);
  };

  const handleBuildReport = async (code) => {
    if (buildingCode) return;
    setBuildingCode(code);
    try {
      const result = await buildLevelTestReportByCodeAPI(code);
      if (result?.status === "done") {
        setBuildingCode("");
        await reload();
        if (result.report_url) window.open(result.report_url, "_blank");
        return;
      }
      pollReport(code, 0);
    } catch (requestError) {
      const message =
        requestError?.response?.data?.error ||
        "리포트를 만들지 못했습니다. 잠시 뒤 다시 시도해 주세요.";
      alert(message);
      setBuildingCode("");
    }
  };

  useEffect(() => {
    if (demoData) return undefined;
    let isCurrent = true;
    fetchCodeVaultAPI()
      .then((result) => {
        if (isCurrent) setData(result);
      })
      .catch(() => {
        if (isCurrent) setError("코드를 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.");
      });
    return () => {
      isCurrent = false;
    };
  }, [demoData]);

  const usableList = useMemo(() => data?.usable ?? [], [data]);
  const usedList = useMemo(() => data?.used ?? [], [data]);

  // 가장 먼저 사라지는 코드. 학원이 제일 먼저 알아야 하는 숫자다.
  const nearestExpiry = useMemo(() => {
    const dayList = usableList
      .map((item) => daysLeft(item.expires))
      .filter((day) => day !== null);
    return dayList.length ? Math.min(...dayList) : null;
  }, [usableList]);

  const abandonedCount = usedList.filter((item) => item.is_abandoned).length;

  const handleCopy = (item) => {
    (navigator.clipboard ? navigator.clipboard.writeText(item.guide_message) : Promise.resolve()).then(() => {
      setCopiedCode(item.code);
      setTimeout(() => setCopiedCode(""), 1600);
    });
  };

  if (error) {
    return (
      <div className="code-vault">
        <p className="code-vault__warn">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="code-vault">
        <p className="code-vault__sub">불러오는 중입니다.</p>
      </div>
    );
  }

  // 이 화면은 계약한 학원의 '코드함' 탭에서도 열린다(LevelTestManagement).
  // 등급을 보지 않고 '미계약' 배지와 도입 안내를 늘 띄우던 탓에, 계약해서 쓰고 있는
  // 학원이 자기 화면에서 미계약이라는 말을 보고 문의했다 (2026-08-31 강릉혜윰학원).
  // 서버가 주는 is_light 로만 가른다. 구 서버가 잠깐 공존하면 이 값이 없으므로,
  // 없을 때는 예전처럼 라이트로 본다.
  const isLight = data.is_light !== false;

  return (
    <div className="code-vault">
      <header className="code-vault__head">
        <h2>응시코드</h2>
        <span className="code-vault__org">{data.academy}</span>
        <span className="code-vault__spacer" />
        {isLight && (
          <span className="code-vault__plan">레벨테스트 미계약</span>
        )}
      </header>

      <div className="code-vault__stats">
        <div className="code-vault__stat code-vault__stat--accent">
          <span className="code-vault__stat-key">쓸 수 있는 코드</span>
          <span className="code-vault__stat-value">{usableList.length}</span>
          <span className="code-vault__stat-note">학생 한 명당 한 장</span>
        </div>
        <div className="code-vault__stat">
          <span className="code-vault__stat-key">응시한 학생</span>
          <span className="code-vault__stat-value">{usedList.length}</span>
          <span className="code-vault__stat-note">
            {usedList.length ? "아래에서 리포트를 봅니다" : "아직 없어요"}
          </span>
        </div>
        {nearestExpiry !== null && (
          <div
            className={`code-vault__stat${
              nearestExpiry <= EXPIRY_WARNING_DAYS ? " code-vault__stat--warn" : ""
            }`}
          >
            <span className="code-vault__stat-key">가장 이른 만료</span>
            <span className="code-vault__stat-value">
              {nearestExpiry >= 0 ? `D-${nearestExpiry}` : "지남"}
            </span>
            <span className="code-vault__stat-note">
              안 쓰면 그냥 사라집니다
            </span>
          </div>
        )}
      </div>

      <section className="code-vault__section">
        <div className="code-vault__section-head">
          <h3>학부모에게 보내기</h3>
          <span className="code-vault__sub">
            코드마다 아래 문구가 그대로 복사됩니다
          </span>
        </div>
        <div className="code-vault__panel">
          <ol className="code-vault__steps">
            <li>
              보낼 코드의 <strong>안내 문구 복사</strong>를 누르세요.
            </li>
            <li>
              학부모에게 붙여 넣으면 학생이 <strong>코드와 이름, 학년</strong>을 넣고
              시험을 시작합니다.
            </li>
            <li>
              시험은 <strong>75문항</strong>이고 한 번에 끝내야 합니다.
            </li>
            <li>
              끝나면 리포트가 자동으로 만들어져 <strong>아래에 나타납니다.</strong>
            </li>
          </ol>
        </div>
      </section>

      {usableList.length > 0 && (
        <section className="code-vault__section">
          <div className="code-vault__section-head">
            <h3>쓸 수 있는 코드</h3>
            <span className="code-vault__sub">{usableList.length}장</span>
          </div>
          <div className="code-vault__cards">
            {usableList.map((item) => {
              const day = daysLeft(item.expires);
              const isNear = day !== null && day <= EXPIRY_WARNING_DAYS;
              return (
                <div className="code-vault__card" key={item.code}>
                  <div className="code-vault__card-top">
                    <span className="code-vault__code">{item.code}</span>
                    <span className="code-vault__chip code-vault__chip--source">
                      {item.source}
                    </span>
                  </div>
                  <div>
                    <span
                      className={`code-vault__chip${
                        isNear ? " code-vault__chip--near" : ""
                      }`}
                    >
                      {item.expires ? `${formatDate(item.expires)}까지` : "기간 제한 없음"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`code-vault__button code-vault__button--ghost code-vault__button--wide${
                      copiedCode === item.code ? " code-vault__button--done" : ""
                    }`}
                    onClick={() => handleCopy(item)}
                  >
                    {copiedCode === item.code ? "복사했어요" : "안내 문구 복사"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="code-vault__section">
        <div className="code-vault__section-head">
          <h3>학생 리포트</h3>
        </div>

        {usedList.length === 0 ? (
          <div className="code-vault__empty">
            <p className="code-vault__empty-title">아직 응시한 학생이 없어요</p>
            <p>
              코드를 받은 학생이 시험을 마치면 여기에 이름과 점수, 리포트가 쌓입니다.
            </p>
            <a
              className="code-vault__button code-vault__button--ghost"
              href={data.demo_report_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              리포트 예시 열어보기
            </a>
          </div>
        ) : (
          <>
            {abandonedCount > 0 && (
              <p className="code-vault__warn">
                리포트 없이 소진된 코드가 {abandonedCount}장 있습니다. 학생이 시험을
                시작만 하고 나가면 코드가 그대로 없어집니다.
              </p>
            )}
            <div className="code-vault__table-wrap">
              <table className="code-vault__table">
                <thead>
                  <tr>
                    <th>학생</th>
                    <th>학년</th>
                    <th>응시일</th>
                    <th>점수</th>
                    <th>리포트</th>
                  </tr>
                </thead>
                <tbody>
                  {usedList.map((item) => (
                    <tr key={item.code}>
                      <td>
                        <span className="code-vault__cell-strong">
                          {item.student_name}
                        </span>
                        <div className="code-vault__cell-code">{item.code}</div>
                      </td>
                      <td>{item.grade}</td>
                      <td>{formatDate(item.used_at)}</td>
                      <td>
                        {item.total_count ? (
                          <>
                            <span className="code-vault__score">
                              {item.right_count}
                            </span>
                            {` / ${item.total_count}`}
                          </>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>
                        {item.report_url ? (
                          <a
                            className="code-vault__button code-vault__button--ghost"
                            href={item.report_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            열기
                          </a>
                        ) : item.report_building ? (
                          <span className="code-vault__button code-vault__button--ghost">
                            만드는 중…
                          </span>
                        ) : item.buildable_session_id ? (
                          // 시험은 끝났는데 리포트만 없는 자리. 눌러서 만든다.
                          <button
                            className="code-vault__button code-vault__button--ghost"
                            onClick={() => handleBuildReport(item.code)}
                            disabled={buildingCode === item.code}
                          >
                            {buildingCode === item.code
                              ? "만드는 중…"
                              : "리포트 만들기"}
                          </button>
                        ) : (
                          <span className="code-vault__warn">리포트 없음</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      {isLight && (
      <section className="code-vault__section">
        <div className="code-vault__section-head">
          <h3>레벨테스트를 도입하면 열리는 기능</h3>
          <span className="code-vault__sub">눌러서 무엇인지 확인해 보세요</span>
        </div>

        <div className="code-vault__locks">
          {LOCKED_FEATURE_LIST.map((feature) => (
            <button
              type="button"
              className="code-vault__lock"
              key={feature.title}
              onClick={() => setOpenedFeature(feature)}
            >
              <span className="code-vault__lock-title">{feature.title}</span>
              <span className="code-vault__lock-desc">
                {openedFeature?.title === feature.title
                  ? feature.detail
                  : feature.summary}
              </span>
            </button>
          ))}
        </div>

        <div className="code-vault__pitch">
          <h3>지금은 코드만 쓰실 수 있어요</h3>
          <p>
            레벨테스트는 온라인 과제와 별도로 신청하는 상품입니다. 도입하시면 위 네
            가지가 함께 열립니다.
          </p>
          <ul>
            <li>
              원생 수에 맞춰 코드가 <strong>달마다 자동으로</strong> 나갑니다
            </li>
            <li>
              응시 안내와 결과 알림톡을 <strong>학부모에게 직접</strong> 보냅니다
            </li>
            <li>
              시험 뒤 <strong>복습까지</strong> 이어져 다음 달 성적으로 연결됩니다
            </li>
          </ul>
          <div className="code-vault__row">
            <a
              className="code-vault__button"
              href={data.store_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              도입하기
            </a>
            <a
              className="code-vault__button code-vault__button--ghost"
              href={data.demo_report_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              리포트 예시 보기
            </a>
          </div>
        </div>
      </section>
      )}

      <p className="code-vault__foot">
        코드는 학생이 <strong>시험을 시작하는 순간</strong> 사용 처리됩니다. 중간에 나가도
        되돌릴 수 없으니, 학생이 끝까지 볼 수 있는 시간에 안내해 주세요.
      </p>
    </div>
  );
};

export default LevelTestCodeVault;
